import { Button, Modal, Textarea } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { formatDay, formatTime } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Daily.module.css';
import { CHART_KINDS, chartScale, reportBadge, ringRatio } from './logic.js';
import type { DailyChartDay, DailyPlanData, DailyReportItem } from './types.js';

/** Hata kodunun metni (`daily.errors.<kod>`; bilinmiyorsa genel ileti). */
export function useErrorText() {
  const { t, i18n } = useTranslation();
  return (error: unknown): string => {
    const code = (error as { code?: string } | null)?.code ?? '';
    const key = `daily.errors.${code}`;
    return code && i18n.exists(key) ? t(key) : t('daily.errors.generic');
  };
}

/** Yerel (gemi saati) tarih-saat değeri `YYYY-MM-DDTHH:mm` ↔ UTC ISO. */
export function localValueOf(utc: string, offsetMin: number): string {
  return new Date(Date.parse(utc) + offsetMin * 60_000).toISOString().slice(0, 16);
}

export function utcOfLocal(local: string, offsetMin: number): string {
  return new Date(Date.parse(`${local}:00.000Z`) - offsetMin * 60_000).toISOString();
}

/** "Today's Summary" (BGM): halka (tamamlanan/toplam), durum sayıları ve departman çubukları. */
export function SummaryCard({ data }: { data: Pick<DailyPlanData, 'summary' | 'day' | 'today'> }) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.summary.${key}`, options ?? {});
  const s = data.summary;
  const ratio = ringRatio(s.completed, s.total);
  const inProgress = ringRatio(s.inProgress, s.total);
  // Halka: tamamlanan (yeşil), devam eden (mavi); çevre 2πr, r = 42
  const circumference = 2 * Math.PI * 42;
  const rows = [
    { key: 'completed', value: s.completed, dot: 'success' },
    { key: 'inProgress', value: s.inProgress, dot: 'primary' },
    { key: 'planned', value: s.planned, dot: 'info' },
    { key: 'overdue', value: s.overdue, dot: 'muted' },
  ];
  return (
    <section className={classes.card} aria-labelledby="daily-summary-title">
      <h2 id="daily-summary-title" className={classes.cardTitle}>
        {data.day === data.today ? k('title') : k('titleDay')}
      </h2>
      <div className={classes.summaryTop}>
        <div
          className={classes.ring}
          role="img"
          aria-label={k('ringAria', { done: s.completed, total: s.total })}
        >
          <svg viewBox="0 0 100 100" aria-hidden>
            <circle className={classes.ringTrack} cx="50" cy="50" r="42" />
            <circle
              className={classes.ringProgress}
              cx="50"
              cy="50"
              r="42"
              strokeDasharray={`${inProgress * circumference} ${circumference}`}
              strokeDashoffset={-ratio * circumference}
            />
            <circle
              className={classes.ringDone}
              cx="50"
              cy="50"
              r="42"
              strokeDasharray={`${ratio * circumference} ${circumference}`}
            />
          </svg>
          <span className={classes.ringValue}>
            <strong>{k('ring', { done: s.completed, total: s.total })}</strong>
            <span>{k('ringLabel')}</span>
          </span>
        </div>
        <ul className={classes.legend}>
          {rows.map((row) => (
            <li key={row.key}>
              <span className={classes.dot} data-tone={row.dot} aria-hidden />
              <span className={classes.legendLabel}>{k(row.key)}</span>
              <span className={classes.legendValue}>{row.value}</span>
            </li>
          ))}
        </ul>
      </div>
      <h3 className={classes.subTitle}>{k('byDepartment')}</h3>
      {s.byDepartment.length === 0 ? (
        <p className={classes.muted}>{k('noTasks')}</p>
      ) : (
        <ul className={classes.bars}>
          {s.byDepartment.map((d) => (
            <li key={d.department}>
              <span className={classes.barLabel}>{t(`daily.departments.${d.department}`)}</span>
              <span
                className={classes.bar}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={d.total}
                aria-valuenow={d.done}
                aria-label={t(`daily.departments.${d.department}`)}
              >
                <span
                  className={classes.barFill}
                  data-department={d.department}
                  style={{ width: `${ringRatio(d.done, d.total) * 100}%` }}
                />
              </span>
              <span className={classes.barValue}>
                {d.done}/{d.total}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** "Reports by Type (Last 7 Days)" (BGM): gün gün dört türün sütunları. */
export function ReportsChart({ chart }: { chart: readonly DailyChartDay[] }) {
  const { t } = useTranslation();
  const max = Math.max(0, ...chart.flatMap((d) => CHART_KINDS.map((kind) => d.counts[kind] ?? 0)));
  const { top, ticks } = chartScale(max);
  const total = chart.reduce(
    (sum, d) => sum + CHART_KINDS.reduce((s, kind) => s + (d.counts[kind] ?? 0), 0),
    0,
  );
  return (
    <section className={classes.card} aria-labelledby="daily-chart-title">
      <div className={classes.cardHead}>
        <h2 id="daily-chart-title" className={classes.cardTitle}>
          {t('daily.chart.title')}{' '}
          <span className={classes.titleNote}>{t('daily.chart.last7')}</span>
        </h2>
        <ul className={classes.chartLegend}>
          {CHART_KINDS.map((kind) => (
            <li key={kind}>
              <span className={classes.swatch} data-kind={kind} aria-hidden />
              {t(`daily.kinds.${kind}`)}
            </li>
          ))}
        </ul>
      </div>
      {total === 0 ? (
        <p className={classes.muted}>{t('daily.chart.none')}</p>
      ) : (
        <figure className={classes.chart} aria-label={t('daily.chart.aria')}>
          <div className={classes.axis} aria-hidden>
            {[...ticks].reverse().map((tick) => (
              <span key={tick}>{tick}</span>
            ))}
          </div>
          <div className={classes.plot}>
            {chart.map((day) => (
              <div key={day.day} className={classes.column}>
                <div className={classes.columnBars}>
                  {CHART_KINDS.map((kind) => {
                    const value = day.counts[kind] ?? 0;
                    return (
                      <span
                        key={kind}
                        className={classes.columnBar}
                        data-kind={kind}
                        style={{ height: `${(value / top) * 100}%` }}
                        title={`${t(`daily.kinds.${kind}`)}: ${value}`}
                      />
                    );
                  })}
                </div>
                <span className={classes.columnLabel}>{formatDay(day.day).slice(0, 5)}</span>
              </div>
            ))}
          </div>
          <ul className={classes.srOnly}>
            {chart.map((day) => (
              <li key={day.day}>
                {formatDay(day.day)}:{' '}
                {CHART_KINDS.map(
                  (kind) => `${t(`daily.kinds.${kind}`)}: ${day.counts[kind] ?? 0}`,
                ).join(', ')}
              </li>
            ))}
          </ul>
        </figure>
      )}
    </section>
  );
}

/** "Recent Reports" (BGM): tarih, tür, durum, gönderen; "View All" Daily Reports'a gider. */
export function RecentReports({
  items,
  names,
  onViewAll,
  onOpen,
}: {
  items: readonly DailyReportItem[];
  names: Readonly<Record<string, string>>;
  onViewAll: () => void;
  onOpen: (item: DailyReportItem) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`daily.recent.${key}`);
  return (
    <section className={classes.card} aria-labelledby="daily-recent-title">
      <div className={classes.cardHead}>
        <h2 id="daily-recent-title" className={classes.cardTitle}>
          {k('title')}
        </h2>
        <Button size="xs" variant="default" onClick={onViewAll}>
          {k('viewAll')}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className={classes.muted}>{k('empty')}</p>
      ) : (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th scope="col">{k('date')}</th>
                <th scope="col">{k('type')}</th>
                <th scope="col">{k('status')}</th>
                <th scope="col">{k('submittedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const badge = reportBadge(item);
                return (
                  <tr key={item.id}>
                    <td>
                      <button
                        type="button"
                        className={classes.linkCell}
                        onClick={() => onOpen(item)}
                      >
                        {formatDay(item.reportDay)}
                      </button>
                    </td>
                    <td>{t(`daily.kinds.${item.kind}`)}</td>
                    <td>
                      <StatusBadge tone={badge.tone} withIcon={false}>
                        {t(`daily.reportStatuses.${badge.key}`)}
                      </StatusBadge>
                    </td>
                    <td className={classes.wrapCell}>
                      {item.submittedBy ? (names[item.submittedBy] ?? '—') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Saat aralığı: "08:00 - 10:00". */
export function timeRange(start: string, end: string | null): string {
  return end ? `${start} - ${end}` : start;
}

/** Tarih-saat, gemi saatiyle. */
export function shipDateTime(at: string, offset: number): string {
  return `${formatDay(localValueOf(at, offset).slice(0, 10))} ${formatTime(at, offset)}`;
}

/**
 * Gerekçe ve/veya parola isteyen onay penceresi (ADR 0051 §6): onay, iade, yeniden açma, taslaktan
 * vazgeçme, görev iptali ve tamamlama. Parola kapatılamaz (MEPC.312 4.3.6).
 */
export function ConfirmDialog({
  opened,
  title,
  text,
  reasonLabel,
  reasonRequired = false,
  password = false,
  confirmLabel,
  danger = false,
  onClose,
  onConfirm,
  children,
}: {
  opened: boolean;
  title: string;
  text?: string;
  reasonLabel?: string | undefined;
  reasonRequired?: boolean;
  password?: boolean;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: (input: { reason: string; password: string }) => Promise<void>;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [reason, setReason] = useState('');
  // Günlük raporda parola isteğe bağlı (ADR 0056): alan yerine bilgi metni; şirket ayarı açıksa ya
  // da sunucu isterse alan görünür. Parola yalnız girildiyse dolu gelir
  const reauth = useReauth('daily_report');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setReason('');
    reauth.reset();
    setError(null);
    onClose();
  };
  const ready =
    (!reasonRequired || reason.trim() !== '') && (!password || !reauth.missing) && !busy;
  return (
    <Modal opened={opened} onClose={close} title={title} centered>
      <form
        className={classes.dialog}
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready) return;
          setBusy(true);
          setError(null);
          onConfirm({
            reason: reason.trim(),
            password: password ? (reauth.body().password ?? '') : '',
          })
            .then(close)
            .catch((failure: unknown) => {
              reauth.noteError(failure);
              setError(errorText(failure));
            })
            .finally(() => setBusy(false));
        }}
      >
        {text ? <p className={classes.muted}>{text}</p> : null}
        {children}
        {reasonLabel ? (
          <Textarea
            label={reasonLabel}
            required={reasonRequired}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            autosize
            minRows={3}
            maxLength={2000}
            data-autofocus
          />
        ) : null}
        {password ? (
          <ReauthField
            reauth={reauth}
            label={t('daily.dialogs.password')}
            description={t('daily.dialogs.passwordHint')}
            mb="0"
          />
        ) : null}
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={close} disabled={busy}>
            {t('daily.dialogs.cancel')}
          </Button>
          <Button
            type="submit"
            {...(danger ? { color: 'red' } : {})}
            loading={busy}
            disabled={!ready}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
